import React from 'react';
import { Rocket, Smartphone, Chrome, BookOpen, GraduationCap, Users, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { SEO, servicesPageJsonLd, breadcrumbs } from '../components/SEO';

export const Services: React.FC = () => {
  const { t } = useLanguage();

  const servicesData = [
    {
      icon: <Rocket className="w-5 h-5" />,
      title: t('services', 'development.title'),
      description: t('services', 'development.description'),
      features: t('services', 'developmentFeatures') as unknown as string[]
    },
    {
      icon: <Smartphone className="w-5 h-5" />,
      title: t('services', 'mobile.title'),
      description: t('services', 'mobile.description'),
      features: t('services', 'mobileFeatures') as unknown as string[]
    },
    {
      icon: <Chrome className="w-5 h-5" />,
      title: t('services', 'extensions.title'),
      description: t('services', 'extensions.description'),
      features: t('services', 'extensionsFeatures') as unknown as string[]
    },
    {
      icon: <BookOpen className="w-5 h-5" />,
      title: t('services', 'ebooks.title'),
      description: t('services', 'ebooks.description'),
      features: t('services', 'ebooksFeatures') as unknown as string[]
    },
    {
      icon: <GraduationCap className="w-5 h-5" />,
      title: t('services', 'courses.title'),
      description: t('services', 'courses.description'),
      features: t('services', 'coursesFeatures') as unknown as string[]
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: t('services', 'consulting.title'),
      description: t('services', 'consulting.description'),
      features: t('services', 'consulting.features') as unknown as string[]
    }
  ];

  return (
    <div className="pt-32 pb-20">
      <SEO 
        title="Services"
        description="Professional web development, mobile apps, browser extensions, and consulting services by Hüseyin Cem Ateş."
        canonical="/services"
        jsonLd={{ "@context": "https://schema.org", "@graph": [servicesPageJsonLd, breadcrumbs.services] }}
      />
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
            {t('services', 'pageTitle')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('services', 'pageSubtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, index) => (
            <div
              key={index}
              className="group bg-card border border-border rounded-2xl p-6 hover:border-foreground/20 transition-all duration-300"
            >
              {/* Icon */}
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-5 group-hover:bg-foreground group-hover:text-background transition-colors">
                {service.icon}
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {Array.isArray(service.features) && service.features.slice(0, 3).map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <div className="w-1 h-1 rounded-full bg-foreground/40 mt-2 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all"
              >
                {t('services', 'learnMore')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Link
              to="/consultation"
              className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {t('services', 'bookConsultation') || 'Book a Consultation'}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/portfolio"
              className="inline-flex items-center justify-center gap-2 border border-border px-6 py-3 rounded-lg font-medium hover:bg-muted transition-colors"
            >
              {t('services', 'viewPortfolio') || 'View Portfolio'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};